import React from 'react'
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react'
import { navigate } from 'gatsby'
import pluralize from 'pluralize'

import {
    ActionItemFieldsFragment,
    ActionItemType,
} from '~graphql/generated/graphql'
import { formatHumanReadableDate } from '~components/chats/chat/messages-list/message-content/message-helpers'
import {
    getPetSelfieDisplayUrl,
    getPetSelfieTitle,
} from '~components/patient-portal/pet-selfies/helpers'
import { UserRoutes } from '~config/routes'
import { ActionItemCard } from './action-item-block'
import { SharedTypeIcon } from './action-item-content/shared'

/**
 * Each preview loads the client's original upload (no thumbnails are
 * generated), so only the newest few selfies get one.
 */
const MAX_PREVIEW_SELFIES = 3

interface Props {
    /** Pending `PetSelfieSubmitted` action items, newest first. */
    petSelfieActionItems: ActionItemFieldsFragment[]
}

/**
 * A single "Action Required" row standing in for every pending pet selfie.
 * Each submission creates its own action item, but reviewing them is one
 * task on the Pet Selfies page, so this shows a count and a few previews
 * instead of a card per submission.
 */
export default function PetSelfieSummaryBlock({ petSelfieActionItems }: Props) {
    const selfies = petSelfieActionItems.flatMap(item =>
        item.pet_selfie ? [item.pet_selfie] : [],
    )
    const latestSelfie = selfies[0]
    const previewSelfies = selfies.slice(0, MAX_PREVIEW_SELFIES)
    const count = petSelfieActionItems.length
    const title = `${count} new ${pluralize('pet selfie', count)} to review`

    return (
        <ActionItemCard>
            <Box w={{ base: 'full', md: 'fit-content' }}>
                <SharedTypeIcon type={ActionItemType.PetSelfieSubmitted} />
            </Box>
            <Box flex={1} w="full">
                <Flex
                    mt={1}
                    justify="space-between"
                    alignItems="center"
                    direction="row"
                    gap={3}
                >
                    <Flex alignItems="center" gap={3} minW={0}>
                        <Flex flexShrink={0}>
                            {previewSelfies.map((selfie, index) => (
                                <Image
                                    key={selfie.id}
                                    src={getPetSelfieDisplayUrl(selfie)}
                                    alt={getPetSelfieTitle(selfie)}
                                    boxSize="40px"
                                    objectFit="cover"
                                    rounded="md"
                                    borderWidth={2}
                                    borderColor="white"
                                    bg="gray.100"
                                    loading="lazy"
                                    position="relative"
                                    zIndex={previewSelfies.length - index}
                                    ml={index === 0 ? 0 : -3}
                                />
                            ))}
                        </Flex>
                        <Box minW={0}>
                            <Text fontWeight="semibold" noOfLines={1}>
                                {title}
                            </Text>
                            {latestSelfie && (
                                <Text
                                    fontSize="sm"
                                    color="gray.600"
                                    noOfLines={1}
                                >
                                    Latest: {getPetSelfieTitle(latestSelfie)} —{' '}
                                    {formatHumanReadableDate(
                                        latestSelfie.created_at,
                                    )}
                                </Text>
                            )}
                        </Box>
                    </Flex>
                    <Button
                        size="xs"
                        shadow="sm"
                        colorScheme="brand"
                        onClick={() => navigate(UserRoutes.PetSelfies)}
                    >
                        Review
                    </Button>
                </Flex>
            </Box>
        </ActionItemCard>
    )
}
